/* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing legacy code, not touched in this pass; typing this properly is tracked separately */
// Auth disabled by request — only used by the now-disabled asyncRequests map.
// import { Record } from "utils/types";
import { storage, StoreInterface } from "api/storageApi";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { API_BASE_URL, getStuffingResultPath, PALLET_VALIDATION_PATH, STUFFING_PATH } from "constants/url.constants";
import { CheckLimitResponseType } from "./response.types";

export enum CheckLimitErrorTypes {
  TIMEOUT = "TimeoutError",
  TOKEN = "TokenError",
}

type TokenSuccess = Partial<{
  "s-token": string;
}>;

type TokenMessage = Partial<{
  message: string;
}>;

export type CheckResponse = {
  profile_id: number | null;
  profile_access: boolean;
  logo: string;
  has_access: boolean;
};

type TokenResponse = TokenMessage | TokenSuccess;

type TokenRequestParams = { params?: { id?: number } };

class HttpsService {
  private _http: AxiosInstance | null = null;
  protected domainUrl: string = "";

  constructor(domain: string) {
    this.http = axios.create({
      baseURL: domain,
      headers: {
        "Content-Type": "application/json",
      },
    });
    this.domainUrl = domain;
  }

  protected async get<T, D extends AxiosRequestConfig>(path: string, data: D): Promise<AxiosResponse<T>> {
    return await this.http.get(path, data);
  }

  set http(httpInstance: AxiosInstance) {
    this._http = httpInstance;
  }

  get http() {
    return this._http as AxiosInstance;
  }
}

class TokenApiService extends HttpsService {
  protected store;
  private _token = "";
  isAuth: boolean;

  constructor(domain: string, store?: StoreInterface) {
    super(domain);
    this.store = store;
    this.isAuth = false;
  }

  static payloadTokenParse = (token: string) => {
    return JSON.parse(atob(token.split(".")[1]));
  };

  async requestUserToken(platformId?: number, authTokenUrl?: string) {
    const callback = (response: AxiosResponse<TokenResponse>) => {
      if (response.data && "s-token" in response.data) {
        this.store?.setData("s-token", response.data["s-token"]);
        this.token = response.data["s-token"] || "";
      }
      return response;
    };
    if (AUTH_TOKEN) {
      return Promise.resolve({
        data: {
          "s-token": AUTH_TOKEN,
        },
      } as AxiosResponse<TokenResponse>).then(callback);
    }
    return await this.get<TokenResponse, TokenRequestParams>(authTokenUrl ?? `${API_BASE_URL}/auth/platform-token`, {
      ...(platformId
        ? {
            params: {
              id: platformId,
            },
          }
        : {}),
    }).then(callback);
  }

  async checkToken() {
    if (this.token) {
      return await this.get<CheckResponse, AxiosRequestConfig>(`${API_BASE_URL}/stuffing/check`, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
    }
  }

  getUserToken(id?: number, authTokenUrl?: string) {
    if (!this.store) return this.requestUserToken(id, authTokenUrl);

    if (this.store.getData("s-token")) {
      const cachedToken = this.store?.getDataWithExp("s-token", this.setTokenExpiredDate(this.store.token));
      if (cachedToken) {
        this.token = cachedToken;
        return cachedToken;
      }
    }

    return this.requestUserToken(id, authTokenUrl);
  }

  setIsAuth() {
    if (this.token) {
      const payload = TokenApiService.payloadTokenParse(this.token) as { profile: boolean };
      if ("profile" in payload) {
        this.isAuth = payload.profile as boolean;
      }
    }
  }

  getIsAuth() {
    return this.isAuth;
  }

  setTokenExpiredDate(token: string) {
    if (token) {
      const payload = TokenApiService.payloadTokenParse(token) as { exp: number };
      if ("exp" in payload) {
        // payload return timestamp in seconds
        return (payload.exp as number) * 1000;
      }
    }

    return 0;
  }

  set token(token: string) {
    this._token = token;
  }

  get token() {
    return this._token;
  }
}

const TokenApi = new TokenApiService(API_BASE_URL, storage as unknown as StoreInterface);

// Auth disabled by request — only used by the now-disabled getToken() below.
// const asyncRequests: Record<string, boolean> = {};

const getResponseByReqUuid = async (uuid: string, token: string | null) => {
  const startTime = Date.now();
  const maxDuration = 60 * 60 * 1000; // 1 hour

  const getResult = async (): Promise<unknown> => {
    try {
      const response = await axios.get(`${API_BASE_URL}${getStuffingResultPath(uuid)}`, {
        withCredentials: false,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const isStatusFailed = response.data?.status === "failed";
      const isStatusCompleted = response.data?.status === "completed";
      const isTimeout = Date.now() - startTime > maxDuration;

      if (isStatusFailed) {
        throw response.data?.error;
      }

      if (isTimeout) {
        throw new Error("Request timeout");
      }

      if (isStatusCompleted) {
        return response.data;
      }

      await new Promise((resolve) => setTimeout(resolve, 5000));
      return getResult();
    } catch (error) {
      throw error;
    }
  };

  return getResult();
};

export const stuffingRequest = async (store: any) => {
  const TOKEN = storage.getData("s-token");

  const getRequestUuid = await axios
    .post(`${API_BASE_URL}${STUFFING_PATH}`, store, {
      withCredentials: false,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-api-key": "sk_Vy9XxR1msrmIDrxLX57QqgnyVj5igbt5sx6dDkgkdeadd466",
        // Authorization: `Bearer ${TOKEN}`,
      },
    })
    .then((res) => res)
    .catch((error) => {
      throw error;
    });

  if (Array.isArray(getRequestUuid?.data) || getRequestUuid?.data?.result?.length) {
    return getRequestUuid;
  }

  const uuidParam = getRequestUuid.data?.uuid;

  if (!uuidParam) {
    throw new Error("Request UUID is not defined");
  }

  return getResponseByReqUuid(uuidParam, TOKEN);
};

// Usage-limit check disabled by request — the /access/check call is no longer made.
// export const checkLimit = async (): Promise<AxiosResponse<CheckLimitResponseType>> => {
//   const TOKEN: string | null = storage.getData("s-token");
//   if (!TOKEN) {
//     throw new Error(CheckLimitErrorTypes.TOKEN);
//   }
//
//   const controller = new AbortController();
//   const signal = controller.signal;
//
//   const TIMEOUT = 20000;
//
//   const timeoutPromise = new Promise<never>((_, reject) => {
//     setTimeout(() => {
//       controller.abort();
//       reject(new Error(CheckLimitErrorTypes.TIMEOUT));
//     }, TIMEOUT);
//   });
//
//   const requestPromise = axios.post<CheckLimitResponseType>(
//     url.getFullURL(`/access/check`),
//     { app: "lc" },
//     {
//       withCredentials: false,
//       headers: {
//         Accept: "application/json",
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${TOKEN}`,
//       },
//       signal,
//     }
//   );
//
//   try {
//     return await Promise.race([requestPromise, timeoutPromise]);
//   } catch (error) {
//     if (
//       (error instanceof Error && error.message.includes(CheckLimitErrorTypes.TIMEOUT)) ||
//       (axios.isAxiosError(error) && error.response?.status === 504)
//     ) {
//       throw new Error(CheckLimitErrorTypes.TIMEOUT);
//     }
//     throw error;
//   }
// };
export const checkLimit = async (): Promise<AxiosResponse<CheckLimitResponseType>> => {
  throw new Error(CheckLimitErrorTypes.TOKEN);
};

export const palletCheckRequest = async (store: any) => {
  return await axios.post(`${API_BASE_URL}${PALLET_VALIDATION_PATH}`, store, {
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

// Auth disabled by request — this legacy helper (already unused elsewhere in
// the app) fetched/validated a platform token; kept for reference only.
/*
// Get user token or check current token on access
export const getToken = async (platformId: number) => {
  const auth = {
    // demo: true,
    demo: false,
    user: false,
    has_access: false,
  };

  const checkToken = async () => {
    const TOKEN = storage.getData("s-token");
    if (!asyncRequests[checkToken.name]) asyncRequests[checkToken.name] = true;
    else return;

    // check access to product by token
    await axios({
      method: "get",
      url: url.getFullURL(`/stuffing/check`),
      withCredentials: false,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${TOKEN}`,
      },
    })
      .then(async (res: any) => {
        if (
          res.data.type === "tokenInvalid" ||
          res.data.error?.message === "Incorrect Token" ||
          res.data.error?.message === "Expired token"
        ) {
          localStorage.removeItem("s-token");
          await getUserToken();
        } else {
          // auth.demo = !res.data.profile_access;
          auth.demo = false;
          auth.user = res.data;
          auth.has_access = res.data.has_access;
        }
      })
      .finally(() => {
        asyncRequests[checkToken.name] = false;
      });
  };

  const getUserToken = async () => {
    if (!asyncRequests[getUserToken.name]) asyncRequests[getUserToken.name] = true;
    else return;

    await axios
      .get(url.getFullURL(`/auth/platform-token?id=${platformId}`))
      .then(async (res) => {
        // set user token
        localStorage.setItem("s-token", res.data["s-token"]);
        // check access after
        await checkToken();
      })
      .finally(() => {
        asyncRequests[getUserToken.name] = false;
      });
  };

  const TOKEN = storage.getData("s-token");
  if (TOKEN) {
    await checkToken();
  } else {
    await getUserToken();
  }

  return auth;
};
*/

export { TokenApi, TokenApiService };
